import React from "react";
import { Button } from "@/components/ui/button";
import dayjs from "dayjs";
import Divider from "@tripbuilder/cat-experiences/divider";
import { formatImageUrl, toTitleCase } from "@/lib/general";
import { type ActivityItem as ActivityItemType, TripLodging } from "@/types";
import useAccommodationStore from "@/stores/tripBuilder/useAccommodation";
import useViewStore from "@/stores/tripBuilder/useView";
import { HomeIcon } from "@heroicons/react/24/outline";

interface LodgingProps {
  lodging: TripLodging;
  locationTimeZone: string;
  nextActivity: ActivityItemType | null;
  activities: ActivityItemType[];
  locationName: string;
  selectedDay: string | null;
  isAdmin: boolean;
}

const Lodging: React.FC<LodgingProps> = ({
  lodging,
  locationTimeZone,
  nextActivity,
  activities,
  locationName,
  selectedDay,
  isAdmin,
}) => {
  const { removeLodging, handleModalOpenTripLodging } = useAccommodationStore();
  const { setActiveTab } = useViewStore();
  const checkInTime = dayjs(lodging.policies?.checkIn || lodging.start_time).tz(
    locationTimeZone
  );
  const checkOutTime = dayjs(lodging.policies?.checkOut).tz(locationTimeZone);

  const getTimeText = () => {
    const today = dayjs(selectedDay).tz(locationTimeZone).startOf("day");
    const startDate = checkInTime.startOf("day");
    const endDate = checkOutTime?.startOf("day");
    const lodgingType = lodging.type === "home" ? "home stay" : lodging.type;

    if (today.isSame(startDate, "day")) {
      return `Check in at ${checkInTime.format("h:mm A")}`;
    } else if (endDate && today.isSame(endDate, "day")) {
      return `Check out by ${checkOutTime.format("h:mm A")}`;
    } else {
      return `Your ${lodgingType} in ${locationName}`;
    }
  };

  const handleDelete = () => {
    removeLodging(lodging.id);
  };

  const handleInfoClick = () => {
    handleModalOpenTripLodging(lodging);
    setActiveTab("accommodation");
  };

  return (
    <div className="w-full">
      <div className="bg-neutral-100/70 rounded-xl py-3 px-4 w-full my-3">
        <div className="flex flex-row items-center justify-between">
          <div className="flex flex-row items-center space-x-4">
            <HomeIcon className="w-5 h-5 text-neutral-600 2xl:w-6 2xl:h-6" />
            <div className="flex flex-col items-start">
              <p className="text-sm text-neutral-800 font-medium mb-0.5 2xl:text-base">
                <span className="text-neutral-800">{lodging.name}</span>
                <span className="text-sm text-neutral-500 font-light ml-1.5 2xl:text-base">
                  {locationName}
                </span>
              </p>
              <p className="text-xs text-neutral-500 font-light 2xl:text-sm">
                {getTimeText()}
              </p>
            </div>
          </div>

          <div className="flex flex-row items-center space-x-2 2xl:space-x-3">
            {lodging.mainImageURL && (
              <img
                src={formatImageUrl(lodging.mainImageURL, "low")}
                alt={lodging.name}
                className="w-8 h-8 rounded-md object-cover 2xl:w-10 2xl:h-10"
              />
            )}
            {lodging.type !== "custom" && (
              <Button
                size="icon"
                variant="ghost"
                className="text-neutral-600 h-8 w-8 2xl:h-10 2xl:w-10 hover:bg-neutral-200 transition rounded-full ease-in-out duration-500 hover:text-neutral-700"
                onClick={handleInfoClick}
              >
                <i className="fal fa-circle-info text-sm 2xl:text-base" />
              </Button>
            )}
            {isAdmin && (
              <Button
                size="icon"
                variant="ghost"
                className="text-neutral-600 h-8 w-8 2xl:h-10 2xl:w-10 hover:bg-rose-50 hover:text-rose-500 duration-300 ease-in-out rounded-full"
                onClick={handleDelete}
              >
                <i className="fal fa-trash text-sm" />
              </Button>
            )}
          </div>
        </div>
      </div>

      {nextActivity && (
        <Divider
          prevItem={lodging as ActivityItemType}
          nextItem={nextActivity}
          locationName={locationName}
          dayItems={activities}
        />
      )}
    </div>
  );
};

export default Lodging;
